import { makeStyles } from '@material-ui/core'
import * as React from 'react'
import Slider from '@material-ui/core/Slider'
import { useState } from 'react'
import PickerIcon from 'images/color_picker.png'
import { colorNameToHex, hexToHsl, hslToHex } from '../../../utils/colorUtils'

const useStyles = makeStyles((theme) => {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    colorSample: {
      width: '82px',
      height: '82px',
      borderRadius: '82px',
    },
    colorContainer: {
      position: 'relative',
      display: 'flex',
      gap: theme.spacing(2),
    },
    w200: {
      maxWidth: '200px',
    },
    picker: {
      position: 'absolute',
      top: 0,
      left: '50px',
      width: '32px',
      height: '32px',

      '& img': {
        width: '100%',
        height: '100%',
      },
      '& input': {
        width: '0',
        height: '0',
        opacity: 0,
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: -1,
      },
    },
  }
})

interface PaletteEditorProps {
  colors: string[]
  onChange: (value: string[]) => void
  onNameChange: (value: string) => void
  name: string
}

const PaletteEditor = ({ colors, onChange, onNameChange, name }: PaletteEditorProps) => {
  const classes = useStyles()

  const [h, s] = hexToHsl(colors[1])

  const [secondaryShade, setSecondaryShade] = useState(hexToHsl(colors[0])[2])
  const [tertiaryShade, setTertiaryShade] = useState(hexToHsl(colors[2])[2])

  const handlePrimaryChange = (
    event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
    shouldNormalize: boolean
  ) => {
    const primaryColor = shouldNormalize ? colorNameToHex(event.target.value) : event.target.value
    const [h1, s1] = hexToHsl(shouldNormalize ? primaryColor : colorNameToHex(primaryColor))
    const secondaryColor = hslToHex(h1, Math.max(s1 - 10, 20), secondaryShade)
    const tertiaryColor = hslToHex(h1, Math.min(s1 + 10, 100), tertiaryShade)
    onChange([primaryColor, secondaryColor, tertiaryColor])
  }

  return (
    <div className={classes.container}>
      <div className='column field'>
        <label>Scheme name</label>
        <div className='control'>
          <input
            autoComplete='none'
            className='input'
            required
            placeholder='My custom scheme'
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
          />
        </div>
      </div>
      <div className={classes.colorContainer}>
        <div className={classes.colorSample} style={{ backgroundColor: colors[1] }} />
        <div className={`column field p-0 ${classes.w200}`}>
          <label className='mb-2' style={{ display: 'block' }}>
            Primary color
          </label>
          <div className='control'>
            <input
              autoComplete='none'
              className='input'
              required
              placeholder='#f9f6e3'
              value={colors[1]}
              onChange={(event) => handlePrimaryChange(event, false)}
              onBlur={(event) => {
                handlePrimaryChange(event, true)
              }}
            />
          </div>
          <label className={classes.picker}>
            <img src={PickerIcon} alt='Color Picker' />
            <input type='color' value={colors[1]} onChange={(event) => handlePrimaryChange(event, false)} />
          </label>
        </div>
      </div>
      <div className={classes.colorContainer}>
        <div className={classes.colorSample} style={{ backgroundColor: colors[0] }} />
        <div className={`column field p-0 ${classes.w200}`}>
          <label className='mb-2' style={{ display: 'block' }}>
            Secondary color
          </label>
          <Slider
            value={secondaryShade}
            max={95}
            min={50}
            step={1}
            onChange={(_event, value) => {
              setSecondaryShade(value as number)
              const col = hslToHex(h, Math.max(s - 10, 20), value as number)
              onChange([colors[1], col, colors[2]])
            }}
          />
        </div>
      </div>
      <div className={classes.colorContainer}>
        <div className={classes.colorSample} style={{ backgroundColor: colors[2] }} />
        <div className={`column field p-0 ${classes.w200}`}>
          <label className='mb-2' style={{ display: 'block' }}>
            Tetriary color
          </label>
          <Slider
            value={tertiaryShade}
            max={40}
            min={5}
            step={1}
            onChange={(_event, value) => {
              setTertiaryShade(value as number)
              const col = hslToHex(h, Math.min(s + 10, 100), value as number)
              onChange([colors[1], colors[0], col])
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default PaletteEditor
